// @ts-nocheck
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { listUserPersonas } from "../graphql/queries";
import { generateClient } from "aws-amplify/api";
import { deleteUserPersona } from "../graphql/mutations";
import { summarizePersona as summarizePersonaApi, SummarizePersonaInput } from "../services/aiAgentService";
import { createUserPersonaCustom, updateUserCustom } from "../graphql/customMutations";
import { getMeUser } from "./userSlice";

const client = generateClient();

const initialState = {
  loading: "idle",
  error: null,
  personas: {},
};

export const fetchPersonas = createAsyncThunk(
  "personas/fetchAllPersonas",
  async (_, thunkAPI) => {
    try {
      const { data } = await client.graphql({ query: listUserPersonas });
      const personaDict = data.listUserPersonas.items.reduce(
        (acc, curr) => ({ ...acc, [curr.id]: curr }),
        {}
      );
      return personaDict;
    } catch (error) {
      console.log("fetch personas error", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const postPersona = createAsyncThunk(
  "personas/postPersona",
  async (input, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: createUserPersonaCustom,
        variables: { input },
      });
      return data.createUserPersona;
    } catch (error) {
      console.log("post persona error", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deletePersonaFunc = createAsyncThunk(
  "personas/deletePersona",
  async (input, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: deleteUserPersona,
        variables: { input },
      });

      // If the deleted persona was the user's preferred persona, clear it
      const state = thunkAPI.getState();
      const userId = state.user?.permissionDetails?.id;
      const preferredPersonaId = state.user?.permissionDetails?.preferredPersonaId;
      if (userId && preferredPersonaId === input.id) {
        await thunkAPI.dispatch(setPreferredPersona({ userId, personaId: null }));
        thunkAPI.dispatch(getMeUser(userId));
      }

      return data.deleteUserPersona;
    } catch (error) {
      console.log("delete persona error", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const summarizePersona = createAsyncThunk(
  "personas/summarizePersona",
  async (input: SummarizePersonaInput, thunkAPI) => {
    try {
      return await summarizePersonaApi(input);
    } catch (error) {
      console.log("summarize persona error", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const setPreferredPersona = createAsyncThunk(
  "personas/setPreferredPersona",
  async ({ userId, personaId }: { userId: string; personaId: string | null }, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: updateUserCustom,
        variables: {
          input: {
            id: userId,
            preferredPersonaId: personaId,
          },
        },
      });
      return data.updateUser;
    } catch (error) {
      console.log("set preferred persona error", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const PersonaSlice = createSlice({
  name: "personas",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPersonas.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(fetchPersonas.fulfilled, (state, action) => {
        state.loading = "success";
        state.personas = action.payload;
      })
      .addCase(fetchPersonas.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message;
      })
      .addCase(postPersona.pending, (state) => {
        state.error = null;
      })
      .addCase(postPersona.fulfilled, (state, action) => {
        state.loading = "success";
        state.personas[action.payload.id] = action.payload;
      })
      .addCase(postPersona.rejected, (state) => {
        state.error = null;
      })
      .addCase(deletePersonaFunc.pending, (state) => {
        state.error = null;
      })
      .addCase(deletePersonaFunc.fulfilled, (state, action) => {
        delete state.personas[action.payload.id];
      })
      .addCase(deletePersonaFunc.rejected, (state) => {
        state.error = null;
      })
      .addCase(summarizePersona.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(summarizePersona.fulfilled, (state) => {
        // Summary is intentionally not stored — it's one-time use, read directly from the thunk result in OnboardingV2.tsx
        state.loading = "success";
      })
      .addCase(summarizePersona.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message;
      });
  },
});

export default PersonaSlice.reducer;
